'use client';

import {
  getProductQuantityRules,
  hasDimensions,
  normalizeProductQuantity,
} from '@eliadmin/core/src/utils/product-utils';
import type { CartItemPackages } from '@eliadmin/core/src/api/cart/cart-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { AddToCartDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import { AddToCartVariantDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useAddToCart } from '@eliadmin/core/src/hooks/cart/use-add-to-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SelectedServices } from '@eliadmin/core/src/stores/variant-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { LuPlus } from 'react-icons/lu';

type AddToCartButtonProps = ButtonProps & {
  product: ProductDetailData | Product;
  quantity?: number;
  className?: string;
  showIcon?: boolean;
  sellable?: boolean;
  customText?: string;
  type?: string;
  customValuesError?: boolean;
  skipVariantDialog?: boolean;
  inCard?: boolean;
  fullSize?: boolean;
};

export function AddToCartButton({
  className,
  showIcon,
  sellable,
  product,
  customText,
  quantity = 1,
  customValuesError = false,
  skipVariantDialog = false,
  inCard = false,
  fullSize = false,
  size,
  ...props
}: AddToCartButtonProps) {
  const isDesktop = useBreakpoint('md');
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const openDialog = useDialogStore(s => s.open);
  const { me, isInitialized } = useCustomerStore();

  const addToCartMutation = useAddToCart();
  const [isAutoAdding, setIsAutoAdding] = useState(false);

  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedPackages = useVariantsStore(state => state.selectedPackages);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const customValues = useVariantsStore(state => state.customValues);
  const selectedAccessories = useVariantsStore(state => state.selectedAccessories);
  const accessoriesQuantities = useVariantsStore(state => state.accessoriesQuantities);

  const productQuery = useQuery({
    queryKey: [CacheKey.PRODUCTS, 'detail', locale, product.id, me?.customer.id ?? null],
    queryFn: () =>
      fetchSlugDetail({ slug: product.slug, locale, authToken: me?.authToken })
        .then(res => res['App\\Models\\Product'])
        .catch(() => null),
    enabled: false,
  });

  const sortedSuppliers = [...product.suppliers].sort((a, b) => a.position - b.position);

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const [currencyISO, setCurrencyISO] = useState<string>('EUR');

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES, locale],
    queryFn: () => fetchCurrencies({ locale }),
  });

  useEffect(() => {
    if (currencies) {
      setCurrencyISO(
        currencies.data.find(currency => currency.symbol === currencySymbol)?.id ?? currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  const firstSupplier = sortedSuppliers[0];
  const hasUnresolvedVariants =
    (!!product.has_variants || !!product.variants?.length) && !selectedVariant;
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product);
  const priceOnRequest = !hasUnresolvedVariants && priceDisplay.priceOnRequest;

  const handleAddToCart = async () => {
    if (!isInitialized || priceOnRequest || (!sellable && quantity < 1)) return;

    const desiredQuantity = normalizeProductQuantity(
      quantity,
      getProductQuantityRules(product, selectedVariant),
    );

    // The variant is already chosen on the product detail (and in comparison), so re-asking for it
    // in the variant dialog only cost the customer a second confirmation click. The dialog is still
    // needed for accessories and range (custom dimension) attributes, which it collects.
    if (selectedVariant && !selectedAccessories?.length && !hasDimensions(product.attributes)) {
      await addToCartMutation.mutateAsync({
        locale,
        body: {
          item: {
            product: { id: product.id },
            variant: { id: selectedVariant.id },
            sub_items: {
              services: selectedServices.map(service => ({ id: service.id })),
              packages: selectedPackages.map(pkg => ({ ...pkg, selected: true })),
            },
          },
          qty: desiredQuantity,
        },
      });
      const variantPrice = priceDisplay.currentWithVat;
      if (variantPrice != null) {
        trackEcommerce.addToCart({
          id: product.id.toString(),
          name: product.name,
          price: variantPrice,
          currency: currencyISO,
        });
      }
      openDialog({
        id: AddToCartDialog.id,
        content: (
          <VariantsProvider
            key={product.id ? `product-${product.id}` : `product-${product.slug}`}
            state={{
              product,
              selectedVariant,
              selectedQuantity: desiredQuantity,
              selectedServices,
              selectedPackages,
              selectedValues,
            }}
          >
            <AddToCartDialog title="text631" product={product} />
          </VariantsProvider>
        ),
      });
      return;
    }

    const fullProduct = product.has_variants
      ? await productQuery.refetch().then(res => res.data)
      : product;

    if (fullProduct && (fullProduct.variants?.length || selectedAccessories?.length)) {
      openDialog({
        id: AddToCartVariantDialog.id,
        content: (
          <VariantsProvider
            key={fullProduct.id ? `product-${fullProduct.id}` : `product-${fullProduct.slug}`}
            state={{
              product: fullProduct,
              selectedPackages,
              selectedAccessories,
              accessoriesQuantities,
              selectedVariant,
              selectedQuantity,
              selectedServices,
              selectedValues,
              customValues,
              customValuesError,
            }}
          >
            <AddToCartVariantDialog
              product={fullProduct}
              quantity={desiredQuantity}
              skipVariantDialog={skipVariantDialog}
              onAutoAddStateChange={setIsAutoAdding}
            />
          </VariantsProvider>
        ),
      });
    } else {
      await addToCartMutation.mutateAsync({
        locale,
        body: {
          item: {
            product: { id: product.id },
            sub_items: {
              services: selectedServices
                ? selectedServices.map((s: SelectedServices) => ({ id: s.id }))
                : [],
              packages: selectedPackages
                ? selectedPackages.map(p => ({ id: p.id }) as CartItemPackages)
                : [],
            },
          },
          qty: desiredQuantity,
        },
      });
      const productPrice = priceDisplay.currentWithVat;
      if (productPrice != null) {
        trackEcommerce.addToCart({
          id: product.id.toString(),
          name: product.name,
          price: productPrice,
          currency: currencyISO,
        });
      }
      openDialog({
        id: AddToCartDialog.id,
        content: <AddToCartDialog product={product} title="text631" />,
      });
    }
  };

  // The customer session resolves a second or two after hydration. The button used to stay disabled
  // until then, silently swallowing early clicks, so customers had to click several times. Remember
  // the click instead and run it as soon as the session is known; running it from an effect (not a
  // store subscription) guarantees the add-to-cart mutation already carries the fresh auth token.
  const [pendingAdd, setPendingAdd] = useState(false);
  useEffect(() => {
    if (!pendingAdd || !isInitialized) return;
    setPendingAdd(false);
    handleAddToCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAdd, isInitialized]);

  const buttonSize = fullSize || (inCard && isDesktop) ? 'default' : 'icon-scaling';
  const effectiveSize = size ?? buttonSize;
  const hideText =
    (typeof effectiveSize === 'string' && effectiveSize.includes('icon')) ||
    (!fullSize && inCard && !isDesktop);

  const isProductEnded = product.status === 'ended';

  const buttonContent = (
    <>
      {showIcon && <LuPlus size={16} className="text-white" />}
      <span className={cn('line-clamp-1 text-ellipsis', hideText && 'hidden')}>
        {customText ? t(customText) : t('text1115')}
      </span>
    </>
  );

  if (!firstSupplier?.is_affiliate && priceOnRequest) return null;

  if (firstSupplier?.is_affiliate) {
    return (
      <Button
        className={cn(className)}
        asChild
        variant="default"
        size={effectiveSize}
        isLoading={addToCartMutation.isPending || productQuery.isFetching}
        {...props}
      >
        <a href={firstSupplier.url ?? '#'}>
          {showIcon && <LuPlus size={16} className="text-white" />}
          <span className="line-clamp-1 text-ellipsis text-center">
            {customText ? t(customText) : t('text1226')}
          </span>
        </a>
      </Button>
    );
  }

  return (
    <Button
      className={cn(className)}
      onClick={e => {
        e.stopPropagation();
        e.nativeEvent.preventDefault();
        if (!isInitialized) {
          setPendingAdd(true);
          return;
        }
        handleAddToCart();
      }}
      disabled={!sellable || isProductEnded || customValuesError}
      variant="default"
      size={effectiveSize}
      isLoading={
        pendingAdd || addToCartMutation.isPending || productQuery.isFetching || isAutoAdding
      }
      {...props}
    >
      {buttonContent}
    </Button>
  );
}
